import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ButtonComponent from '../../components/button/ButtonComponent';
import { VerificationBannerContext } from '../../components/layout/dashboard-layout';
import CommonTable from '../../components/tableComponent/commonTableComponent';
import { AccordionTableDetailsWrapper } from '../../components/tableComponent/CommonTableStyles';
import { colors } from '../../components/theme/theme';
import NoRecordsMessage from '../../components/ui-components/NoRecordsMessage';
import CommonStatusChip from '../../components/ui-components/StatusChipComponent';
import { OpenInvoiceApiResponse } from '../../services/payment-services';
import { RootState } from '../../store';
import { showLoader } from '../../store/reducers/appSlice';
import { formatCurrency, formatToUSDate, shouldDisablePayNow } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { paginateRows } from '../../utils/paymentHistoryHelpers';
import { useTranslation } from '../../utils/translations';

import { UpcomingPaymentMobileCard } from './components/UpcomingPaymentMobileCard';

export interface PendingScheduleAmountItem {
    contractId: string;
    totalPendingScheduleAmount: number;
}

interface ScheduledPaymentData {
    AspireContractId?: string;
    aspireContractId?: string;
    ContractId?: string;
    contractId?: string;
    Status?: string | number;
    status?: string | number;
    DueDate?: string;
    PaymentDate?: string;
    Amount?: number | string;
    amount?: number | string;
}

interface UpcomingPaymentsProps {
    currentPage?: number;
    rowsPerPage?: number;
    onTotalCountChange?: (count: number) => void;
    invoiceData?: OpenInvoiceApiResponse[];
    pendingScheduleAmount?: PendingScheduleAmountItem[];
    scheduledPaymentsData?: ScheduledPaymentData[];
    onNavigateToScheduledPayments?: (contractId?: string) => void;
}

const UpcomingPayments: React.FC<UpcomingPaymentsProps> = ({
    currentPage = 0,
    rowsPerPage = 10,
    onTotalCountChange,
    invoiceData,
    pendingScheduleAmount = [],
    scheduledPaymentsData = [],
    onNavigateToScheduledPayments,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

    const showVerificationBanner = useContext(VerificationBannerContext);
    const isLoading = useSelector((state: RootState) => state.app.activeHTTPRequests > 0);
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const userContracts = useSelector((state: RootState) => state.contracts?.contracts || []);

    const onNavigateToScheduledPaymentsRef = useRef<((contractId?: string) => void) | undefined>();

    useEffect(() => {
        onNavigateToScheduledPaymentsRef.current = onNavigateToScheduledPayments;
    }, [onNavigateToScheduledPayments]);

    /** Cancelled/failed status values (must match ContractEnums.PaymentStatusTypes.cancelled/failed). Avoids enum key casing mismatch between source and .d.ts. Failed payments never reduce remaining due — they carry no real money. */
    const CANCELLED_PAYMENT_STATUS = 2;
    const FAILED_PAYMENT_STATUS = 3;
    const isScheduledPaymentPending = (s: ScheduledPaymentData) => {
        const status = s?.Status ?? s?.status;
        if (status == null) return true;
        if (typeof status === 'string') {
            const statusStr = String(status).toLowerCase();
            return statusStr !== 'cancelled' && statusStr !== 'failed';
        }
        return status !== CANCELLED_PAYMENT_STATUS && status !== FAILED_PAYMENT_STATUS;
    };

    const pendingOnlyScheduledPayments = useMemo(
        () => (scheduledPaymentsData || []).filter(isScheduledPaymentPending),
        [scheduledPaymentsData],
    );

    const effectivePendingByContract = useMemo(() => {
        const byContract = new Map<string, number>();
        for (const s of pendingOnlyScheduledPayments) {
            const cid = String(s.AspireContractId ?? s.aspireContractId ?? s.ContractId ?? s.contractId ?? '');
            if (!cid) continue;
            const amount = Number(s.Amount ?? s.amount ?? 0);
            byContract.set(cid, (byContract.get(cid) ?? 0) + amount);
        }
        return byContract;
    }, [pendingOnlyScheduledPayments]);

    const getPendingForContract = useCallback(
        (contractId: string | number | undefined) => {
            if (!contractId) return 0;
            const fromApi = pendingScheduleAmount.find(p => String(p.contractId) === String(contractId));
            return fromApi ? Number(fromApi.totalPendingScheduleAmount) : 0;
        },
        [pendingScheduleAmount],
    );

    const getEffectivePendingForContract = useCallback(
        (contractId: string | number | undefined, aspireId?: string) => {
            if (aspireId && effectivePendingByContract.has(aspireId))
                return effectivePendingByContract.get(aspireId) ?? 0;
            if (contractId && effectivePendingByContract.has(String(contractId)))
                return effectivePendingByContract.get(String(contractId)) ?? 0;
            return getPendingForContract(contractId);
        },
        [effectivePendingByContract, getPendingForContract],
    );

    const getInvoiceKey = (invoice: OpenInvoiceApiResponse) => {
        const cid = invoice?.contractId ?? invoice?.ContractId;
        const due = invoice?.DueDate ?? invoice?.duedate;
        const amt = invoice?.amount ?? invoice?.Amount;
        const num = invoice?.InvoiceNumber ?? invoice?.invoiceNumber ?? '';
        return `${String(cid)}_${due}_${amt}_${num}`;
    };

    /** Aspire ID preferred (matches Scheduled tab rows); falls back to DB contract id. */
    const getContractIdForScheduledTabFilter = (invoice: OpenInvoiceApiResponse): string | undefined => {
        const id = String(
            invoice?.aspireContractId ?? invoice?.AspireContractId ?? invoice?.contractId ?? invoice?.ContractId ?? '',
        ).trim();
        return id || undefined;
    };

    type AllocationEntry = { scheduledDate: string; amount: number; originalAmount?: number };
    type AllocationResult = {
        allocated: number;
        remainingDue: number;
        extraToPrincipal: number;
        entries: AllocationEntry[];
    };

    /** Pending-only scheduled entries per contract (for allocation), sorted by date asc. */
    const pendingEntriesByContract = useMemo(() => {
        const byContract = new Map<string, AllocationEntry[]>();
        for (const s of pendingOnlyScheduledPayments) {
            const cid = String(s.AspireContractId ?? s.aspireContractId ?? s.ContractId ?? s.contractId ?? '');
            if (!cid) continue;
            const scheduledDate = s.DueDate ?? s.PaymentDate ?? '';
            const amount = Number(s.Amount ?? s.amount ?? 0);
            if (!scheduledDate || amount <= 0) continue;
            if (!byContract.has(cid)) byContract.set(cid, []);
            byContract.get(cid)!.push({ scheduledDate, amount });
        }
        byContract.forEach(entries => {
            entries.sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime());
        });
        return byContract;
    }, [pendingOnlyScheduledPayments]);

    /** Allocate contract's pending scheduled payments across invoices (oldest first); record which scheduled entries apply to each invoice. */
    const allocationByInvoiceKey = useMemo(() => {
        const map: Record<string, AllocationResult> = {};
        if (!invoiceData?.length) return map;

        const byContract = new Map<string, OpenInvoiceApiResponse[]>();
        for (const inv of invoiceData) {
            const cid = String(inv?.contractId ?? inv?.ContractId ?? '');
            if (!byContract.has(cid)) byContract.set(cid, []);
            byContract.get(cid)!.push(inv);
        }

        for (const [contractId, invoices] of byContract) {
            const aspireId = invoices[0]?.aspireContractId ?? invoices[0]?.AspireContractId ?? contractId;
            const entries = pendingEntriesByContract.get(aspireId) ?? pendingEntriesByContract.get(contractId) ?? [];
            const sorted = [...invoices].sort((a, b) => {
                const dateA = (a.DueDate ?? a.duedate) ? new Date(a.DueDate ?? a.duedate).getTime() : 0;
                const dateB = (b.DueDate ?? b.duedate) ? new Date(b.DueDate ?? b.duedate).getTime() : 0;
                return dateA - dateB;
            });
            const remainingEntries = entries.map(e => ({ ...e, remaining: e.amount }));
            for (const inv of sorted) {
                const amountDue = Number(inv?.amount ?? inv?.Amount ?? 0);
                let need = amountDue;
                const appliedEntries: AllocationEntry[] = [];
                for (const entry of remainingEntries) {
                    if (need <= 0) break;
                    if (entry.remaining <= 0) continue;
                    const take = Math.min(entry.remaining, need);
                    appliedEntries.push({
                        scheduledDate: entry.scheduledDate,
                        amount: take,
                        originalAmount: take < entry.amount ? entry.amount : undefined,
                    });
                    entry.remaining -= take;
                    need -= take;
                }
                const allocated = amountDue - need;
                const remainingDue = Math.max(0, need);
                const overflow = remainingEntries.reduce((sum, e) => sum + e.remaining, 0);
                const extraToPrincipal = remainingDue === 0 ? overflow : 0;
                map[getInvoiceKey(inv)] = { allocated, remainingDue, extraToPrincipal, entries: appliedEntries };
            }
        }
        return map;
    }, [invoiceData, pendingEntriesByContract]);

    const transformedRows = useMemo(() => {
        if (!invoiceData || invoiceData.length === 0) return [];

        const sortedData = [...invoiceData].sort((a, b) => {
            const dateA = (a.DueDate ?? a.duedate) ? new Date(a.DueDate ?? a.duedate).getTime() : 0;
            const dateB = (b.DueDate ?? b.duedate) ? new Date(b.DueDate ?? b.duedate).getTime() : 0;
            return dateB - dateA;
        });

        return sortedData.map((invoice: OpenInvoiceApiResponse) => {
            const hasSegregation = invoice?.segregation && invoice.segregation.length > 0;
            const contractId = (invoice as OpenInvoiceApiResponse).contractId ?? invoice.ContractId;
            const amountDue = Number(invoice.amount ?? invoice.Amount ?? 0);
            const paymentInProgress = getEffectivePendingForContract(
                contractId,
                invoice.aspireContractId ?? invoice.AspireContractId,
            );
            const allocation = allocationByInvoiceKey[getInvoiceKey(invoice)];
            const remainingDue = allocation ? allocation.remainingDue : Math.max(0, amountDue - paymentInProgress);
            const extraToPrincipal = allocation
                ? allocation.extraToPrincipal
                : paymentInProgress > amountDue
                  ? paymentInProgress - amountDue
                  : 0;
            const isCovered = remainingDue === 0 && paymentInProgress > 0;
            const hasAdjustedScheduledPayments = (allocation?.allocated ?? 0) > 0;

            const disablePayNow = shouldDisablePayNow(
                invoice.aspireContractId ?? invoice.AspireContractId,
                userContracts,
            );

            // Fetch autopay status directly from contract's AutoPay field
            const contract = userContracts?.find(
                c =>
                    c.AspireId === (invoice.aspireContractId ?? invoice.AspireContractId) ||
                    c.AspireRecordId === (invoice.aspireContractId ?? invoice.AspireContractId),
            );
            const autoPayEnabled = contract?.AutoPay ? 'Yes' : 'No';

            // Determine tooltip reason for disabled button
            let disabledTooltip = '';
            if (disablePayNow && contract) {
                const isConsumerLoan = contract.AccountType === 'I' && contract.IsLoan;
                if (contract.AutoPay && !isConsumerLoan) {
                    disabledTooltip =
                        t('contracts.details.payNowDisabledAutopay') ||
                        'Payment is managed by AutoPay. Manual payments are not available.';
                }
            }

            const payNowButton = (
                <Stack justifyContent="center" height="100%" spacing={1.5}>
                    <span>
                        <ButtonComponent
                            variant="contained"
                            sx={{
                                width: 'fit-content',
                                minWidth: 100,
                                fontWeight: 600,
                                transition: 'all 0.2s ease',
                                '&:hover:not(:disabled)': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                },
                            }}
                            disabled={disablePayNow}
                            disabledTooltip={disabledTooltip}
                            onClick={() => {
                                if (!disablePayNow) {
                                    dispatch(showLoader());
                                    const scheduledForThisInvoice = allocation?.allocated ?? paymentInProgress;
                                    navigate(NavigationConfig.DashboardMakePayment, {
                                        state: {
                                            selectedContractIds: [invoice?.aspireContractId],
                                            fromPayments: true,
                                            singleInvoiceOverrides: {
                                                [invoice?.aspireContractId ?? '']: {
                                                    dueAmount: amountDue.toFixed(2),
                                                    scheduledAmount: scheduledForThisInvoice.toFixed(2),
                                                },
                                            },
                                        },
                                    });
                                }
                            }}
                        >
                            {t('dashboard.alerts.payNow')}
                        </ButtonComponent>
                    </span>
                </Stack>
            );

            const remainingDueCell = (
                <Stack
                    alignItems="flex-end"
                    justifyContent="center"
                    spacing={0.25}
                    sx={{
                        maxWidth: '100%',
                        minWidth: 0,
                        flexWrap: 'wrap',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'right',
                    }}
                >
                    <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                                color:
                                    remainingDue > 0 && hasAdjustedScheduledPayments
                                        ? colors.warningPreset[50]
                                        : undefined,
                            }}
                        >
                            {formatCurrency(remainingDue)}
                        </Typography>
                    </Stack>
                    {Number(extraToPrincipal) >= 0.01 && (
                        <Typography variant="caption" color="success.main" fontWeight={500}>
                            + {formatCurrency(extraToPrincipal)} {t('payments.upcomingPayments.extraPrincipal')}
                        </Typography>
                    )}
                    {!isCovered && hasAdjustedScheduledPayments && remainingDue > 0 && (
                        <Stack direction="row" alignItems="center" flexWrap="wrap" justifyContent="flex-end" gap={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                {t('payments.upcomingPayments.inProgressExpandForDetails')}
                            </Typography>
                            <Box
                                component="span"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    const filterId = getContractIdForScheduledTabFilter(invoice);
                                    if (onNavigateToScheduledPaymentsRef.current) {
                                        onNavigateToScheduledPaymentsRef.current(filterId);
                                    } else {
                                        navigate(NavigationConfig.Payments, {
                                            state: { activeTab: 1, ...(filterId ? { contractFilter: filterId } : {}) },
                                        });
                                    }
                                }}
                                sx={{
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    fontWeight: 500,
                                }}
                            >
                                {t('payments.upcomingPayments.viewInScheduledPayments')}
                            </Box>
                        </Stack>
                    )}
                </Stack>
            );

            const actionCell = (
                <Stack justifyContent="center" height="100%" alignItems="center">
                    {payNowButton}
                </Stack>
            );

            const amountDueCell = (
                <Stack alignItems="flex-end" justifyContent="center" spacing={0.25}>
                    <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(amountDue)}
                    </Typography>
                    {hasSegregation && (
                        <Typography variant="caption" color="text.secondary">
                            {t('payments.upcomingPayments.amountIncludesFees')}
                        </Typography>
                    )}
                </Stack>
            );

            const invoiceNumberDisplay = invoice.InvoiceNumber ?? invoice.invoiceNumber ?? '—';

            return {
                dueDate: invoice.duedate
                    ? formatToUSDate(invoice.duedate)
                    : invoice.DueDate
                      ? formatToUSDate(invoice.DueDate)
                      : 'N/A',
                contractId: invoice.aspireContractId || invoice.AspireContractId || 'N/A',
                invoiceNumber: invoiceNumberDisplay,
                amount: amountDueCell,
                remainingDue: remainingDueCell,
                autopayEnabled: autoPayEnabled,
                status: actionCell,
                originalInvoice: invoice,
                hasAccordion: hasSegregation || paymentInProgress > 0 || !disablePayNow,
            };
        });
    }, [
        invoiceData,
        allocationByInvoiceKey,
        t,
        userContracts,
        getEffectivePendingForContract,
        navigate,
        dispatch,
    ]);

    useEffect(() => {
        if (onTotalCountChange) {
            onTotalCountChange(transformedRows.length);
        }
    }, [transformedRows.length, onTotalCountChange]);

    const paginatedRows = useMemo(
        () => paginateRows(transformedRows, currentPage, rowsPerPage),
        [transformedRows, currentPage, rowsPerPage],
    );

    const columns = [
        {
            id: 'contractId',
            label: t('payments.upcomingPayments.contractId') || 'Contract ID',
            sortable: false,
            minWidth: 120,
        },
        {
            id: 'dueDate',
            label: t('payments.upcomingPayments.invoiceDueDate') || 'Invoice Due Date',
            sortable: true,
            minWidth: 160,
        },
        {
            id: 'invoiceNumber',
            label: (t('payments.upcomingPayments.invoiceNumber') || '').trim() || 'Invoice Number',
            sortable: false,
            minWidth: 140,
            align: 'center' as const,
        },
        {
            id: 'amount',
            label: t('payments.upcomingPayments.amount') || 'Amount Due',
            sortable: false,
            minWidth: 160,
            align: 'right' as const,
        },
        {
            id: 'remainingDue',
            label: t('payments.upcomingPayments.remainingDue') || 'Remaining Due',
            sortable: false,
            align: 'right' as const,
            minWidth: 130,
        },
        {
            id: 'autopayEnabled',
            label: t('payments.upcomingPayments.autopayEnabled') || 'Autopay',
            sortable: false,
            align: 'center' as const,
        },
        {
            id: 'status',
            label: t('payments.upcomingPayments.actions') || 'Action',
            sortable: false,
            minWidth: 120,
            align: 'center' as const,
        },
    ];

    if (transformedRows.length === 0 && !isLoading) {
        return <NoRecordsMessage message={t('common.NoRecordsAvailable')} />;
    }

    const renderAccordionContent = (row: Record<string, unknown>) => {
        const invoice = row.originalInvoice as OpenInvoiceApiResponse;
        const hasSegregation = invoice?.segregation && invoice.segregation.length > 0;
        const contractId = (invoice as OpenInvoiceApiResponse)?.contractId ?? invoice?.ContractId;
        const aspireId = invoice?.aspireContractId ?? invoice?.AspireContractId ?? contractId;

        const paymentInProgress = getEffectivePendingForContract(contractId, aspireId);
        const amountDue = Number(invoice?.amount ?? invoice?.Amount ?? 0);
        const allocation = allocationByInvoiceKey[getInvoiceKey(invoice)] as AllocationResult | undefined;
        const totalScheduled = allocation?.allocated ?? 0;
        const remainingDue = allocation?.remainingDue ?? Math.max(0, amountDue - paymentInProgress);
        const extraToPrincipal =
            allocation?.extraToPrincipal ?? (paymentInProgress > amountDue ? paymentInProgress - amountDue : 0);
        const isFullyCovered = remainingDue === 0 && paymentInProgress > 0;
        const scheduledEntries = allocation?.entries ?? [];

        const segregationSum =
            (invoice?.segregation || []).reduce(
                (sum: number, item: { Description: string; Amount: string }) => sum + Number(item.Amount || 0),
                0,
            ) || 0;
        const baseInvoiceAmount = Math.max(0, amountDue - segregationSum);

        const hasFeeSection = hasSegregation || baseInvoiceAmount > 0;
        const hasAllocationToThisInvoice = (allocation?.allocated ?? 0) > 0;
        const hasFlightSection = hasAllocationToThisInvoice;

        const disablePayNow = shouldDisablePayNow(
            invoice.aspireContractId ?? invoice.AspireContractId ?? '',
            userContracts,
        );
        const showNewLayout = !disablePayNow;

        if (!hasFeeSection && !hasFlightSection) {
            return (
                <AccordionTableDetailsWrapper>
                    <Typography variant="body2" color="text.secondary">
                        {t('payments.upcomingPayments.noPaymentsInProgress')}
                    </Typography>
                </AccordionTableDetailsWrapper>
            );
        }

        if (showNewLayout) {
            return (
                <AccordionTableDetailsWrapper
                    sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        paddingRight: 3,
                    }}
                >
                    <Grid container spacing={2} sx={{ width: '100%' }}>
                        <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                            <Stack sx={{ height: '100%' }}>
                                <Typography
                                    variant="subtitle1"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 1.5 }}
                                >
                                    {t('payments.upcomingPayments.amountDueBreakdown')}
                                </Typography>
                                <Stack
                                    sx={{
                                        border: `1px solid ${colors.greyPreset[30]}`,
                                        borderRadius: 2,
                                        p: 2,
                                        height: '100%',
                                    }}
                                >
                                    {baseInvoiceAmount > 0 && (
                                        <Typography
                                            variant="body2"
                                            display={'flex'}
                                            alignItems={'center'}
                                            justifyContent={'space-between'}
                                        >
                                            {t('payments.upcomingPayments.amount')}:{' '}
                                            <Typography variant="subtitle1" component={'span'}>
                                                {formatCurrency(baseInvoiceAmount)}
                                            </Typography>
                                        </Typography>
                                    )}
                                    {(invoice?.segregation || []).map(
                                        (item: { Description: string; Amount: string }, index: number) => (
                                            <Typography
                                                key={index}
                                                variant="body2"
                                                display={'flex'}
                                                alignItems={'center'}
                                                justifyContent={'space-between'}
                                            >
                                                {item.Description}:{' '}
                                                <Typography variant="subtitle1" component={'span'}>
                                                    {formatCurrency(Number(item.Amount))}
                                                </Typography>
                                            </Typography>
                                        ),
                                    )}
                                    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 0.5 }}>
                                        <Typography
                                            variant="subtitle1"
                                            component="h2"
                                            display={'flex'}
                                            alignItems={'center'}
                                            justifyContent={'space-between'}
                                        >
                                            {t('payments.upcomingPayments.totalDue')}:{' '}
                                            <Typography variant="subtitle1" component={'span'}>
                                                {formatCurrency(amountDue)}
                                            </Typography>
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Stack>
                        </Grid>

                        {hasFlightSection && (
                            <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                                <Stack sx={{ height: '100%' }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={700}
                                        color="text.primary"
                                        sx={{ display: 'block', mb: 1.5 }}
                                    >
                                        {t('payments.upcomingPayments.paymentCalculation')}
                                    </Typography>
                                    <Stack
                                        spacing={1.5}
                                        sx={{
                                            border: `1px solid ${colors.greyPreset[30]}`,
                                            borderRadius: 2,
                                            p: 2,
                                            height: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                        >
                                            <span>{t('payments.upcomingPayments.amountDueLabel')}:</span>
                                            <Typography component="span" variant="body2">
                                                {formatCurrency(amountDue)}
                                            </Typography>
                                        </Typography>
                                        <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
                                            <Tooltip
                                                title={t('payments.upcomingPayments.scheduledPaymentTooltip') || ''}
                                                placement="top"
                                                arrow
                                            >
                                                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            </Tooltip>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('payments.upcomingPayments.scheduledPaymentsAppliedToInvoice')}:
                                            </Typography>
                                        </Stack>
                                        {scheduledEntries.length > 0 ? (
                                            <Stack spacing={1}>
                                                {scheduledEntries.map((entry, idx) => (
                                                    <Stack key={idx} spacing={0.5}>
                                                        <Box
                                                            sx={{
                                                                borderLeft: `2px solid ${colors.greyPreset[30]}`,
                                                                pl: 1.5,
                                                                ml: 0.5,
                                                            }}
                                                        >
                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                flexWrap="wrap"
                                                                gap={1}
                                                            >
                                                                <Stack
                                                                    direction="row"
                                                                    alignItems="center"
                                                                    gap={1}
                                                                    flexWrap="wrap"
                                                                >
                                                                    <Typography variant="body2" color="text.primary">
                                                                        {formatToUSDate(entry.scheduledDate)}
                                                                    </Typography>
                                                                    <CommonStatusChip
                                                                        label={t('payments.paymentHistory.pending')}
                                                                        chipBgColor={colors.warningPreset[50]}
                                                                        sx={{ minWidth: 60 }}
                                                                    />
                                                                </Stack>
                                                                <Typography variant="body2" component="span">
                                                                    {formatCurrency(entry.amount)}
                                                                </Typography>
                                                            </Stack>
                                                        </Box>
                                                        {entry.originalAmount != null && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    fontStyle: 'italic',
                                                                    display: 'block',
                                                                    pl: 0.5,
                                                                    ml: 0.5,
                                                                    pt: 0.5,
                                                                }}
                                                            >
                                                                {t(
                                                                    'payments.upcomingPayments.scheduledPortionAdjustedMessage',
                                                                )
                                                                    .replace(
                                                                        '{{amount}}',
                                                                        formatCurrency(entry.originalAmount),
                                                                    )
                                                                    .replace(
                                                                        '{{adjustedAmount}}',
                                                                        formatCurrency(entry.amount),
                                                                    )}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        ) : null}
                                        <Box sx={{ borderTop: `1px solid ${colors.greyPreset[30]}`, pt: 1.5, mt: 0.5 }}>
                                            <Typography
                                                variant="body2"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <span>{t('payments.upcomingPayments.totalApplied')}:</span>
                                                <Typography component="span" variant="body2">
                                                    {formatCurrency(totalScheduled)}
                                                </Typography>
                                            </Typography>
                                        </Box>
                                        <Box sx={{ borderTop: `1px solid ${colors.greyPreset[30]}`, pt: 1.5, mt: 1.5 }}>
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={700}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <span>{t('payments.upcomingPayments.remainingDue')}:</span>
                                                <Typography
                                                    component="span"
                                                    variant="subtitle1"
                                                    fontWeight={700}
                                                    sx={
                                                        remainingDue > 0
                                                            ? { color: colors.warningPreset[50] }
                                                            : undefined
                                                    }
                                                >
                                                    {formatCurrency(remainingDue)}
                                                </Typography>
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Stack>
                            </Grid>
                        )}

                        <Grid size={12} sx={{ gap: 1.5, width: '100%' }}>
                            {paymentInProgress > 0 && hasAllocationToThisInvoice && (
                                <Box
                                    sx={{
                                        bgcolor: colors.bluePreset[50],
                                        border: `1px solid ${colors.greyPreset[30]}`,
                                        borderRadius: 2,
                                        p: 1.5,
                                        color: colors.bluePreset[30],
                                    }}
                                >
                                    <Typography variant="body2">
                                        {(t('payments.upcomingPayments.scheduledAmountBannerMessage') as string)
                                            .split('{{amount}}')
                                            .map((part, i) => (
                                                <React.Fragment key={i}>
                                                    {i === 1 ? (
                                                        <Typography component="span" variant="body2" fontWeight={600}>
                                                            {formatCurrency(paymentInProgress)}
                                                        </Typography>
                                                    ) : null}
                                                    {part}
                                                </React.Fragment>
                                            ))}
                                    </Typography>
                                </Box>
                            )}
                            {isFullyCovered && Number(extraToPrincipal) >= 0.01 && !hasFlightSection && (
                                <Box
                                    sx={{
                                        bgcolor: colors.successPreset[50],
                                        borderRadius: 2,
                                        p: 2,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {t('payments.upcomingPayments.invoiceFullyCovered')}
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </AccordionTableDetailsWrapper>
            );
        }

        return (
            <AccordionTableDetailsWrapper>
                <Grid container spacing={3}>
                    {/* Left: Fee Breakdown */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Stack sx={{ height: '100%' }}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ letterSpacing: 0.5, display: 'block', mb: 1.5 }}
                            >
                                {t('payments.upcomingPayments.feeBreakdown').toUpperCase()}
                            </Typography>
                            <Stack
                                spacing={1}
                                sx={{
                                    border: `1px solid ${colors.greyPreset[30]}`,
                                    borderRadius: 2,
                                    p: 2,
                                    height: '100%',
                                }}
                            >
                                {baseInvoiceAmount > 0 && (
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2">{t('payments.upcomingPayments.amount')}</Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {formatCurrency(baseInvoiceAmount)}
                                        </Typography>
                                    </Stack>
                                )}
                                {(invoice?.segregation || []).map(
                                    (item: { Description: string; Amount: string }, index: number) => (
                                        <Stack key={index} direction="row" justifyContent="space-between">
                                            <Typography variant="body2">{item.Description}</Typography>
                                            <Typography variant="body2" fontWeight={600}>
                                                {formatCurrency(Number(item.Amount))}
                                            </Typography>
                                        </Stack>
                                    ),
                                )}
                                <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 0.5 }}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="subtitle1">
                                            {t('payments.upcomingPayments.totalDue')}
                                        </Typography>
                                        <Typography variant="subtitle1">{formatCurrency(amountDue)}</Typography>
                                    </Stack>
                                </Box>
                            </Stack>
                        </Stack>
                    </Grid>

                    {/* Right: Scheduled Payment (formerly Payments in Flight) */}
                    {hasFlightSection && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ letterSpacing: 0.5, display: 'block', mb: 1.5 }}
                            >
                                {t('payments.upcomingPayments.scheduledPayment').toUpperCase()}
                            </Typography>
                            <Box
                                sx={{
                                    border: `1px solid ${colors.greyPreset[30]}`,
                                    borderRadius: 2,
                                    p: 2,
                                }}
                            >
                                <Stack spacing={1.5}>
                                    <Stack direction="row" justifyContent="space-between" gap={2}>
                                        <Typography variant="body2">
                                            {t('payments.upcomingPayments.amountDueLabel')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {formatCurrency(amountDue)}
                                        </Typography>
                                    </Stack>
                                    {scheduledEntries.length > 0 && (
                                        <>
                                            <Typography variant="body2">
                                                {t('payments.upcomingPayments.scheduledPayment')}
                                            </Typography>
                                            {scheduledEntries.map((entry, idx) => (
                                                <Stack key={idx} spacing={0.5}>
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        gap={2}
                                                        alignItems="center"
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            gap={1}
                                                            flexWrap="wrap"
                                                        >
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatToUSDate(entry.scheduledDate)}
                                                            </Typography>
                                                            <CommonStatusChip
                                                                label={t('payments.paymentHistory.pending')}
                                                                chipBgColor={colors.warningPreset[50]}
                                                                sx={{ minWidth: 60 }}
                                                            />
                                                        </Stack>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {formatCurrency(entry.amount)}
                                                        </Typography>
                                                    </Stack>
                                                    {entry.originalAmount != null && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ pl: 0.5, fontStyle: 'italic', pt: 0.5 }}
                                                        >
                                                            {t(
                                                                'payments.upcomingPayments.scheduledPortionAdjustedMessage',
                                                            )
                                                                .replace(
                                                                    '{{amount}}',
                                                                    formatCurrency(entry.originalAmount),
                                                                )
                                                                .replace(
                                                                    '{{adjustedAmount}}',
                                                                    formatCurrency(entry.amount),
                                                                )}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            ))}
                                        </>
                                    )}
                                    <Stack direction="row" justifyContent="space-between" gap={2}>
                                        <Typography variant="body2">
                                            {t('payments.upcomingPayments.scheduledPaymentTotal')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {formatCurrency(totalScheduled)}
                                        </Typography>
                                    </Stack>
                                    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 0.5 }}>
                                        <Stack direction="row" justifyContent="space-between" gap={2}>
                                            <Typography variant="subtitle1">
                                                {t('payments.upcomingPayments.remainingDue')}
                                            </Typography>
                                            <Typography
                                                variant="subtitle1"
                                                sx={
                                                    remainingDue > 0
                                                        ? { color: colors.warningPreset[50], fontWeight: 600 }
                                                        : undefined
                                                }
                                            >
                                                {formatCurrency(remainingDue)}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                    {paymentInProgress > 0 && hasAllocationToThisInvoice && (
                                        <Box
                                            sx={{
                                                bgcolor: colors.bluePreset[50],
                                                border: `1px solid ${colors.greyPreset[30]}`,
                                                borderRadius: 1,
                                                p: 1.5,
                                                mt: 0.5,
                                                color: colors.bluePreset[30],
                                            }}
                                        >
                                            <Typography variant="caption">
                                                {(t('payments.upcomingPayments.scheduledAmountBannerMessage') as string)
                                                    .split('{{amount}}')
                                                    .map((part, i) => (
                                                        <React.Fragment key={i}>
                                                            {i === 1 ? (
                                                                <Typography
                                                                    component="span"
                                                                    variant="caption"
                                                                    fontWeight={600}
                                                                >
                                                                    {formatCurrency(paymentInProgress)}
                                                                </Typography>
                                                            ) : null}
                                                            {part}
                                                        </React.Fragment>
                                                    ))}
                                            </Typography>
                                        </Box>
                                    )}
                                    {isFullyCovered && Number(extraToPrincipal) >= 0.01 && !hasFlightSection && (
                                        <Box
                                            sx={{
                                                bgcolor: colors.successPreset[50],
                                                borderRadius: 1,
                                                p: 1.5,
                                                mt: 0.5,
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ color: 'white' }}>
                                                {t('payments.upcomingPayments.invoiceFullyCovered')}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box
                                        component="span"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            const filterId = getContractIdForScheduledTabFilter(invoice);
                                            if (onNavigateToScheduledPaymentsRef.current) {
                                                onNavigateToScheduledPaymentsRef.current(filterId);
                                            } else {
                                                navigate(NavigationConfig.Payments, {
                                                    state: {
                                                        activeTab: 1,
                                                        ...(filterId ? { contractFilter: filterId } : {}),
                                                    },
                                                });
                                            }
                                        }}
                                        sx={{
                                            color: 'primary.main',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            mt: 0.5,
                                        }}
                                    >
                                        {t('payments.upcomingPayments.viewInScheduledPayments')}
                                    </Box>
                                </Stack>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </AccordionTableDetailsWrapper>
        );
    };

    return (
        <Stack gap={3}>
            {impersonation?.isImpersonating && (
                <Stack
                    sx={{
                        px: 3,
                        p: 1.5,
                        backgroundColor: colors.warningPreset[40],
                        borderRadius: 1,
                        border: `1px solid ${colors.warningPreset[30]}`,
                    }}
                >
                    <Typography variant="body2" color={colors.primaryLight[100]}>
                        {t('payments.impersonatingNote') ||
                            'The payment button is disabled while impersonating. You can review the payment details but cannot submit the payment.'}
                    </Typography>
                </Stack>
            )}
            {isMobileOrTablet ? (
                <Stack px={{ xs: 1.5, sm: 3 }} gap={1.5}>
                    {paginatedRows.map(row => (
                        <UpcomingPaymentMobileCard
                            key={getInvoiceKey(row.originalInvoice)}
                            row={row}
                            renderAccordionContent={renderAccordionContent}
                        />
                    ))}
                </Stack>
            ) : (
                <Stack px={3}>
                    <CommonTable
                        columns={columns}
                        rows={paginatedRows}
                        enableAccordion={true}
                        renderAccordionContent={renderAccordionContent}
                        stickyHeader={true}
                        maxHeight={
                            showVerificationBanner
                                ? { xs: '100%', lg: 'calc(100dvh - 390px)' }
                                : { xs: '100%', lg: 'calc(100dvh - 355px)' }
                        }
                        className="accordionTableWrapper"
                    />
                </Stack>
            )}
        </Stack>
    );
};

export default UpcomingPayments;
